import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tabs, Tab, CircularProgress } from '@mui/material';
import DocumentManager from './DocumentManager';
import KnowledgeRetrieval from './KnowledgeRetrieval';
import KnowledgeGraph from './KnowledgeGraph';
import { useTranslation } from '../contexts/TranslationContext';
import VideoProvider from '../components/VideoProvider';
import { videoUrls, videoTitles } from '../utils/videoUrls';
import { membership } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`knowledge-tabpanel-${index}`}
      aria-labelledby={`knowledge-tab-${index}`}
      style={{ width: '100%', height: '100%', display: value === index ? 'block' : 'none' }}
    >
      {value === index && (
        <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>{children}</Box>
      )}
    </div>
  );
};

type KnowledgeModule = 'knowledge_list' | 'knowledge_conversation' | 'knowledge_graph';

interface KnowledgeTabConfig {
  module: KnowledgeModule;
  labelKey: string;
  Component: React.ComponentType;
}

const KNOWLEDGE_TAB_CONFIG: KnowledgeTabConfig[] = [
  { module: 'knowledge_list', labelKey: 'knowledgeBase.tabs.tab1', Component: DocumentManager },
  { module: 'knowledge_conversation', labelKey: 'knowledgeBase.tabs.tab2', Component: KnowledgeRetrieval },
  { module: 'knowledge_graph', labelKey: 'knowledgeBase.tabs.tab3', Component: KnowledgeGraph },
];

const tabBarSx = {
  border: '1.5px solid black',
  borderBottom: 'none',
  borderTopRightRadius: { xs: '10px', sm: '14px', md: '16px' },
  borderTopLeftRadius: { xs: '10px', sm: '14px', md: '16px' },
  '& .MuiTabs-flexContainer': { justifyContent: 'center' },
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTab-root': {
    backgroundColor: '#D9D9D980',
    color: '#000',
    fontWeight: 400,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    minWidth: { xs: '60px', sm: '80px', md: '120px' },
    flex: '1 1 0',
    textAlign: 'center',
    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
    px: { xs: 0.75, sm: 1, md: 2 },
    py: { xs: 0.75, sm: 1, md: 1.25 },
  },
  '& .MuiTab-root:not(:last-of-type)': {
    borderRight: '1.5px solid black',
  },
  '& .MuiTab-root:first-of-type': {
    borderTopLeftRadius: { xs: '9px', sm: '13px', md: '15px' },
  },
  '& .MuiTab-root:last-of-type': {
    borderTopRightRadius: { xs: '9px', sm: '13px', md: '15px' },
  },
  '& .MuiTab-root.Mui-selected': {
    backgroundColor: '#009688',
    color: '#fff',
    fontWeight: 700,
  },
};

const KnowledgeBase: React.FC = () => {
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uiModules, setUiModules] = useState<string[] | null>(null);
  const [isMember, setIsMember] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchMembership = async () => {
      try {
        const response = await membership.getMe();
        setIsMember(response.data.is_member);
        setUiModules(response.data.ui_modules ?? []);
      } catch (error) {
        console.error('Failed to fetch membership info:', error);
        setIsMember(false);
        setUiModules(null);
      } finally {
        setLoading(false);
      }
    };

    void fetchMembership();
  }, []);

  const visibleTabs = useMemo(() => {
    if (!isMember) {
      return KNOWLEDGE_TAB_CONFIG;
    }
    if (!uiModules) {
      return [];
    }
    return KNOWLEDGE_TAB_CONFIG.filter((tab) => uiModules.includes(tab.module));
  }, [isMember, uiModules]);

  useEffect(() => {
    if (value >= visibleTabs.length) {
      setValue(0);
    }
  }, [visibleTabs.length, value]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  if (loading) {
    return (
      <VideoProvider videoUrl={videoUrls.knowledge} videoTitle={videoTitles.knowledge}>
        <Box sx={{ width: '100%', height: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </VideoProvider>
    );
  }

  return (
    <VideoProvider videoUrl={videoUrls.knowledge} videoTitle={videoTitles.knowledge}>
      <Box sx={{ width: '100%', height: '90vh', display: 'flex', flexDirection: 'column'}}>
      {visibleTabs.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="knowledge base tabs"
            sx={tabBarSx}
          >
            {visibleTabs.map((tab, index) => (
              <Tab
                key={tab.module}
                label={t(tab.labelKey)}
                id={`knowledge-tab-${index}`}
                aria-controls={`knowledge-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>
      )}
      <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 , border: '1.5px solid black',borderRadius: '20px', padding: {xs: '2px', md: '10px'}, margin: '0 20px'}}>
        {visibleTabs.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
            {t('knowledgeBase.noAccess')}
          </Box>
        ) : (
          visibleTabs.map((tab, index) => {
            const { Component } = tab;
            return (
              <TabPanel key={tab.module} value={value} index={index}>
                <Component />
              </TabPanel>
            );
          })
        )}
      </Box>
      </Box>
    </VideoProvider>
  );
};

export default KnowledgeBase;
